import React from 'react';
import PropTypes from 'prop-types';
import { DateTime } from 'luxon';
import {
  Cushion,
  FlexContainer,
  Grid,
  MaxWidth,
  Rectangle,
  ShimmerTypography,
  Space,
  Typography,
} from '@pitchero/react-ui';
import Icon from '../icon';
import clubUrl from '../../lib/routing/club-url';
import Article from '../article';
import CLUB_PROP_TYPES from '../../lib/prop-types/club';
import { Trans } from '../../lib/i18n';
import RosTopMpu from '../adverts/ros-top-mpu';
import TopBannerVideoPlayerSwitch from '../adverts/top-banner-video-player-switch';
import Comments from '../comments';
import EventAttendeeList from './attendee-list';
import EventAttendance from './event-attendance';
import ClubEventLoadingState from './loading-state';
import RelatedContent from '../related-content';

const ClubEvent = ({
  club,
  isLoading,
  event,
  eventId,
  attendees,
  memberAttendances,
  isLoadingMemberAttendance,
  isLoggedIn,
  onMarkAsAttending,
  onMarkAsNotAttending,
  userIsAssociatedToClub,
}) => {
  const { author, html, image, recurrence, start, end, title } = event;
  const startLuxon = DateTime.fromISO(start).setZone(club.timezone);

  if (isLoading && !title) {
    // If we have no title & isLoading then this needs a full loading placeholder.
    return <ClubEventLoadingState />;
  }

  const { advertisingTier, sportId } = club;

  const shareUrl = clubUrl(club, 'calendar.event', { _title: title, id: eventId });
  const renderSubheader = () => {
    const endLuxon = DateTime.fromISO(end).setZone(club.timezone);
    const multiDay = startLuxon.day !== endLuxon.day;
    return (
      <Cushion vertical="small">
        <FlexContainer flexDirection="row" justifyContent="left" alignItems="center">
          <Cushion right="small">
            <Icon name="calendar" size={32} color="dustygrey" />
          </Cushion>
          <Typography preset="tag--large" color="dustygrey">
            {startLuxon.toFormat(multiDay ? 'cccc d MMMM yyyy, HH:mm' : 'cccc d MMMM yyyy')}
            <br />
            {multiDay
              ? endLuxon.toFormat('cccc d MMMM yyyy, HH:mm')
              : `${startLuxon.toFormat('HH:mm')} - ${endLuxon.toFormat('HH:mm')}`}
            {recurrence && (
              <>
                <br />
                {recurrence}
                <Icon name="substitution" size={14} topOffset={2} color="notification" />
              </>
            )}
          </Typography>
        </FlexContainer>
      </Cushion>
    );
  };

  return (
    <div style={{ background: 'white' }}>
      <Cushion top="medium">
        <TopBannerVideoPlayerSwitch
          advertisingTier={advertisingTier}
          pageType="calendar.event"
          sportId={sportId}
        />
      </Cushion>
      <Cushion
        horizontal="large"
        vertical="medium"
        responsive={[{ maxWidth: 'palm', props: { horizontal: 'small' } }]}
        component="div"
      >
        <MaxWidth component="div" maxWidth={1366} center>
          <Grid preset="clubEvent">
            <Space bottom="medium" style={{ gridArea: 'article' }} component="div">
              <Article
                author={author}
                imageAlt={title}
                imageSrc={image}
                title={title}
                club={club}
                renderSubheader={renderSubheader}
                shareProps={{
                  url: shareUrl,
                  title,
                  imageUrl: image,
                }}
                showHeaderImage
              >
                {isLoading ? (
                  <>
                    <ShimmerTypography preset="body--large" width="100%" />
                    <ShimmerTypography preset="body--large" width="100%" />
                    <ShimmerTypography preset="body--large" width="100%" />
                    <ShimmerTypography preset="body--large" width="80%" />
                  </>
                ) : (
                  <div className="bbcode-content" dangerouslySetInnerHTML={{ __html: html }} />
                )}
              </Article>

              <Space top="medium" component="div">
                <RelatedContent
                  clubId={club.id}
                  messageId={`club_event-${eventId}`}
                  placement="club_event"
                />
              </Space>
            </Space>

            <Space
              className="club-event__sidebar"
              bottom="medium"
              style={{ gridArea: 'attendance' }}
              component="div"
            >
              {startLuxon > DateTime.local() ? (
                <EventAttendance
                  club={club}
                  isLoading={isLoadingMemberAttendance}
                  isLoggedIn={isLoggedIn}
                  onMarkAsAttending={onMarkAsAttending}
                  onMarkAsNotAttending={onMarkAsNotAttending}
                  memberAttendances={memberAttendances}
                  userIsAssociatedToClub={userIsAssociatedToClub}
                />
              ) : (
                <Rectangle fill="negative" radius="rounded">
                  <Cushion all="small">
                    <FlexContainer alignItems="center">
                      <Cushion right="small">
                        <Icon name="info-circle" color="white" size={24} />
                      </Cushion>
                      <Typography color="white" preset="subtitle--small" component="div">
                        <Trans i18nKey="common:past_event_warning">This is a past event</Trans>
                      </Typography>
                    </FlexContainer>
                  </Cushion>
                </Rectangle>
              )}
            </Space>
            <div className="no-grid-hide" style={{ gridArea: 'attendees' }}>
              <EventAttendeeList attendees={attendees} />
              <RosTopMpu />
            </div>

            <Cushion
              className="comments-wrap"
              vertical="medium"
              component="div"
              style={{ gridArea: 'comments' }}
            >
              <Comments club={club} mediaId={eventId} mediaType="event" />
            </Cushion>
          </Grid>
        </MaxWidth>
      </Cushion>
    </div>
  );
};

ClubEvent.propTypes = {
  attendees: PropTypes.arrayOf(
    PropTypes.shape({
      avatar: PropTypes.string,
      name: PropTypes.string,
    }),
  ).isRequired,
  club: CLUB_PROP_TYPES.isRequired,
  event: PropTypes.shape({
    id: PropTypes.number,
    author: PropTypes.shape(),
    html: PropTypes.string,
    image: PropTypes.string,
    title: PropTypes.string,
    recurrence: PropTypes.string,
    start: PropTypes.string,
    end: PropTypes.string,
  }).isRequired,
  eventId: PropTypes.number.isRequired,
  memberAttendances: PropTypes.arrayOf(
    PropTypes.shape({
      avatar: PropTypes.string.isRequired,
      firstName: PropTypes.string.isRequired,
      isAttending: PropTypes.bool.isRequired,
      memberId: PropTypes.number.isRequired,
      name: PropTypes.string.isRequired,
    }),
  ).isRequired,
  isLoading: PropTypes.bool.isRequired,
  isLoadingMemberAttendance: PropTypes.bool.isRequired,
  isLoggedIn: PropTypes.bool.isRequired,
  onMarkAsAttending: PropTypes.func.isRequired,
  onMarkAsNotAttending: PropTypes.func.isRequired,
  userIsAssociatedToClub: PropTypes.bool.isRequired,
};

export default ClubEvent;
